import { useEffect, useRef, useState } from "react";
import PostCallWebhookCard from "@/components/PostCallWebhookCard";
import { Link } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  AudioLines,
  Brain,
  Copy,
  CopyPlus,
  FileText,
  History,
  Loader2,
  Maximize2,
  Mic,
  MoreVertical,
  Pencil,
  Phone,
  PhoneOff,
  PhoneOutgoing,
  Sparkles,
  Trash2,
  UploadCloud,
} from "lucide-react";
import { toast } from "sonner";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import {
  FIRST_MESSAGE_MODE_LABELS,
  assistantsApi,
  type Assistant,
  type AttachedNumber,
  type FirstMessageMode,
  type ProviderOption,
} from "../api";
import { useUpdateAssistant } from "../hooks";
import PlaceCallDialog from "@/features/telephony/components/PlaceCallDialog";
import AssistantAdvanced from "./AssistantAdvanced";
import AssistantAnalysis from "./AssistantAnalysis";
import AssistantCallLogs from "./AssistantCallLogs";
import AssistantTools from "./AssistantTools";
import TalkPanel from "./TalkPanel";
import VersionHistory from "./VersionHistory";
import TranscriberSettings from "./settings/TranscriberSettings";
import ModelSettings from "./settings/ModelSettings";
import VoiceSettings from "./settings/VoiceSettings";

const TABS = ["Assistant", "Logs", "Tools", "Analysis", "Webhook", "Advanced"] as const;
type Tab = (typeof TABS)[number];

/** Where callers actually reach this assistant.
 *
 * Read-only, and links out rather than offering to attach: a number has one
 * "Answered by" picker that owns the mapping, and two screens setting the
 * same thing is how an assistant ends up attached in one place and not the
 * other. The provider is shown because it decides what a call can do — a
 * Twilio leg can't transfer or hang up, so those tools are withheld on it.
 */
function AttachedNumbers({ numbers }: { numbers: AttachedNumber[] }) {
  if (numbers.length === 0) {
    return (
      <div className="mt-1.5 flex items-center gap-1.5 text-xs text-muted-foreground">
        <PhoneOff className="size-3.5" />
        <span>No number attached — reachable only through Talk.</span>
        <Link to="/phone-numbers" className="underline underline-offset-2 hover:text-foreground">
          Attach one
        </Link>
      </div>
    );
  }
  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
      <span className="text-muted-foreground">Answers on</span>
      {numbers.map((n) => (
        <Link
          key={n.id}
          to={n.channel === "whatsapp" ? "/whatsapp" : "/phone-numbers"}
          className="flex items-center gap-1.5 rounded-full border px-2 py-0.5 transition-colors hover:bg-muted"
        >
          <span className="font-mono">{n.number}</span>
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
            {n.channel === "whatsapp" ? "WhatsApp" : n.provider}
          </span>
        </Link>
      ))}
    </div>
  );
}

/** A three-segment proportional bar (transcriber · model · voice). */
function SegmentBar({ parts }: { parts: number[] }) {
  const total = parts.reduce((a, b) => a + b, 0) || 1;
  const colors = ["bg-emerald-500", "bg-sky-500", "bg-fuchsia-500"];
  return (
    <div className="mt-2 flex h-2 w-full overflow-hidden rounded-full bg-muted">
      {parts.map((p, i) => (
        <div key={i} className={cn(colors[i])} style={{ width: `${(p / total) * 100}%` }} />
      ))}
    </div>
  );
}

type ProviderCardProps = {
  label: string;
  dotClass: string;
  icon: React.ComponentType<{ className?: string }>;
  option: ProviderOption | null;
  /** Shown as the card title; defaults to the option name. */
  title?: string;
  onEdit: () => void;
};

function ProviderCard({ label, dotClass, icon: Icon, option, title, onEdit }: ProviderCardProps) {
  return (
    <div className="rounded-xl border bg-card p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          <span className={cn("size-1.5 rounded-full", dotClass)} />
          {label}
        </div>
        <button
          onClick={onEdit}
          className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <Pencil className="size-3.5" />
        </button>
      </div>
      {option ? (
        <>
          <div className="mt-3 flex items-center gap-2">
            <Icon className="size-4 text-muted-foreground" />
            <span className="text-sm font-semibold">{title ?? option.name}</span>
          </div>
          <div className="mt-0.5 text-xs text-muted-foreground">
            {title ? `${option.provider_label} · ${option.name}` : option.provider_label}
          </div>
          <div className="mt-2 flex items-center gap-3 text-xs">
            <span className="text-amber-500">{option.latency_ms}ms</span>
          </div>
        </>
      ) : (
        <>
          <div className="mt-3 flex items-center gap-2">
            <Icon className="size-4 text-muted-foreground" />
            <span className="text-sm font-semibold text-muted-foreground">Not set</span>
          </div>
          <div className="mt-0.5 text-xs text-muted-foreground">
            Pick one from the catalog.
          </div>
        </>
      )}
    </div>
  );
}

type Props = {
  assistant: Assistant;
  /** Called with the new copy's id after Duplicate succeeds. */
  onDuplicated?: (id: string) => void;
  /** Called after Delete succeeds so the page can drop the selection. */
  onDeleted?: () => void;
};

/** The Assistant editor body (Screen 2). Remounted per assistant (keyed by
 * id in AssistantsPage), so local editing state can init from props. */
export default function AssistantEditor({ assistant, onDuplicated, onDeleted }: Props) {
  const qc = useQueryClient();
  const [panel, setPanel] = useState<"transcriber" | "model" | "voice" | null>(null);
  const [tab, setTab] = useState<Tab>("Assistant");
  const [talkOpen, setTalkOpen] = useState(false);
  const [placeCallOpen, setPlaceCallOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [firstMessage, setFirstMessage] = useState(assistant.first_message);
  const [systemPrompt, setSystemPrompt] = useState(assistant.system_prompt);
  const [mode, setMode] = useState<FirstMessageMode>(assistant.first_message_mode);
  const updateMut = useUpdateAssistant(assistant.id);

  // Same assistant id but new server content (e.g. a version restore):
  // adopt it unless the user has unsaved local edits.
  const prevRef = useRef(assistant);
  useEffect(() => {
    const prev = prevRef.current;
    prevRef.current = assistant;
    const hadLocalEdits =
      firstMessage !== prev.first_message ||
      systemPrompt !== prev.system_prompt ||
      mode !== prev.first_message_mode;
    if (!hadLocalEdits) {
      setFirstMessage(assistant.first_message);
      setSystemPrompt(assistant.system_prompt);
      setMode(assistant.first_message_mode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assistant]);

  const duplicateMut = useMutation({
    mutationFn: () => assistantsApi.duplicate(assistant.id),
    onSuccess: (copy) => {
      qc.invalidateQueries({ queryKey: ["assistants"] });
      toast.success(`"${copy.name}" created.`);
      onDuplicated?.(copy.id);
    },
    onError: () => toast.error("Couldn't duplicate the assistant. Please try again."),
  });

  const deleteMut = useMutation({
    mutationFn: () => assistantsApi.remove(assistant.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["assistants"] });
      toast.success(`"${assistant.name}" deleted.`);
      setDeleteOpen(false);
      onDeleted?.();
    },
    onError: () => toast.error("Couldn't delete the assistant. Please try again."),
  });

  const dirty =
    firstMessage !== assistant.first_message ||
    systemPrompt !== assistant.system_prompt ||
    mode !== assistant.first_message_mode;

  const contentPatch = {
    first_message: firstMessage,
    system_prompt: systemPrompt,
    first_message_mode: mode,
  };

  function save() {
    updateMut.mutate(contentPatch, {
      onSuccess: () => toast.success("Assistant saved."),
    });
  }

  function togglePublish() {
    updateMut.mutate(
      { ...contentPatch, is_published: !assistant.is_published },
      {
        onSuccess: () =>
          toast.success(assistant.is_published ? "Assistant unpublished." : "Assistant published."),
      },
    );
  }

  const totalLatency =
    (assistant.transcriber?.latency_ms ?? 0) +
    (assistant.llm?.latency_ms ?? 0) +
    (assistant.voice?.latency_ms ?? 0);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">{assistant.name}</h1>
          <button
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            onClick={() => {
              navigator.clipboard?.writeText(assistant.id);
              toast.success("Assistant ID copied.");
            }}
          >
            <span className="font-mono">{assistant.id}</span>
            <Copy className="size-3" />
          </button>
          <AttachedNumbers numbers={assistant.attached_numbers ?? []} />
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={talkOpen ? "secondary" : "outline"}
            size="sm"
            onClick={() => setTalkOpen((v) => !v)}
          >
            <Phone className="size-4" />
            Talk
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPlaceCallOpen(true)}
          >
            <PhoneOutgoing className="size-4" />
            Place Call
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={!dirty || updateMut.isPending}
            onClick={save}
          >
            {updateMut.isPending && <Loader2 className="size-4 animate-spin" />}
            Save
          </Button>
          <Button size="sm" disabled={updateMut.isPending} onClick={togglePublish}>
            {assistant.is_published ? "Unpublish" : "Publish"}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="px-2">
                <MoreVertical className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setHistoryOpen(true)}>
                <History className="size-4" />
                Version History
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={duplicateMut.isPending}
                onClick={() => duplicateMut.mutate()}
              >
                <CopyPlus className="size-4" />
                Duplicate
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onClick={() => setDeleteOpen(true)}
              >
                <Trash2 className="size-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <PlaceCallDialog
        open={placeCallOpen}
        onOpenChange={setPlaceCallOpen}
        lockedAssistantId={assistant.id}
      />

      {/* Live Talk session (opens from the header Talk button) */}
      {talkOpen && (
        <div className="space-y-1">
          {dirty && (
            <p className="text-xs text-amber-600 dark:text-amber-500">
              Unsaved changes — Save to talk with your latest edits.
            </p>
          )}
          <TalkPanel assistant={assistant} onClose={() => setTalkOpen(false)} />
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={cn(
              "relative px-3 py-2 text-sm font-medium transition-colors",
              t === tab
                ? "text-foreground after:absolute after:inset-x-2 after:-bottom-px after:h-0.5 after:rounded-full after:bg-primary"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === "Logs" && <AssistantCallLogs assistant={assistant} />}

      {tab === "Tools" && <AssistantTools assistant={assistant} />}

      {tab === "Analysis" && <AssistantAnalysis assistant={assistant} />}

      {tab === "Webhook" && <PostCallWebhookCard owner="assistants" id={assistant.id} />}

      {tab === "Advanced" && <AssistantAdvanced assistant={assistant} />}

      {tab === "Assistant" && (
      <>
      {/* Latency (live from the admin-managed provider catalog). Per-minute
       * costs are internal (admin-only) — tenants are on monthly plans. */}
      <div className="rounded-xl border bg-card p-4">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Average Latency
        </div>
        <div className="mt-1 text-lg font-semibold">
          ~{totalLatency.toLocaleString()} <span className="text-sm font-normal text-muted-foreground">ms</span>
        </div>
        <SegmentBar
          parts={[
            assistant.transcriber?.latency_ms ?? 0,
            assistant.llm?.latency_ms ?? 0,
            assistant.voice?.latency_ms ?? 0,
          ]}
        />
      </div>

      {/* Provider cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <ProviderCard
          label="Transcriber"
          dotClass="bg-emerald-500"
          icon={Mic}
          option={assistant.transcriber}
          title={assistant.transcriber ? assistant.transcriber_language : undefined}
          onEdit={() => setPanel("transcriber")}
        />
        <ProviderCard
          label="Model"
          dotClass="bg-sky-500"
          icon={Brain}
          option={assistant.llm}
          onEdit={() => setPanel("model")}
        />
        <ProviderCard
          label="Voice"
          dotClass="bg-fuchsia-500"
          icon={AudioLines}
          option={assistant.voice}
          title={assistant.tts_voice_name || undefined}
          onEdit={() => setPanel("voice")}
        />
      </div>

      {/* First Message */}
      <div className="rounded-xl border bg-card p-4">
        <div className="flex flex-wrap items-center gap-3">
          <Label className="text-sm">First Message</Label>
          <Select value={mode} onValueChange={(v) => setMode(v as FirstMessageMode)}>
            <SelectTrigger className="h-8 w-56">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(FIRST_MESSAGE_MODE_LABELS).map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Textarea
          className="mt-3 min-h-[64px] resize-none"
          value={firstMessage}
          onChange={(e) => setFirstMessage(e.target.value)}
        />
      </div>

      {/* System Prompt */}
      <div className="rounded-xl border bg-card p-4">
        <div className="flex items-center justify-between">
          <Label className="text-sm">System Prompt</Label>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm">
              <Sparkles className="size-3.5" />
              Generate
            </Button>
            <button className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground">
              <Maximize2 className="size-4" />
            </button>
          </div>
        </div>
        <Textarea
          className="mt-3 min-h-[320px] font-mono text-xs leading-relaxed"
          value={systemPrompt}
          onChange={(e) => setSystemPrompt(e.target.value)}
        />
      </div>

      {/* Files */}
      <div className="rounded-xl border bg-card p-4">
        <div className="flex items-center gap-2">
          <FileText className="size-4 text-muted-foreground" />
          <Label className="text-sm">Files</Label>
        </div>
        <Select disabled>
          <SelectTrigger className="mt-3">
            <SelectValue placeholder="Select Files" />
          </SelectTrigger>
          <SelectContent />
        </Select>
        <p className="mt-2 flex items-center gap-1 text-xs text-muted-foreground">
          <UploadCloud className="size-3.5" />
          Knowledge base attachment is coming in a later step.
        </p>
      </div>
      </>
      )}

      {/* Settings slide-overs (open from the edit pencils above) */}
      <TranscriberSettings
        open={panel === "transcriber"}
        onOpenChange={(o) => setPanel(o ? "transcriber" : null)}
        assistant={assistant}
      />
      <ModelSettings
        open={panel === "model"}
        onOpenChange={(o) => setPanel(o ? "model" : null)}
        assistant={assistant}
      />
      <VoiceSettings
        open={panel === "voice"}
        onOpenChange={(o) => setPanel(o ? "voice" : null)}
        assistant={assistant}
      />

      <VersionHistory
        assistant={assistant}
        open={historyOpen}
        onOpenChange={setHistoryOpen}
      />

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{assistant.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes the assistant and its version history.
              This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMut.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteMut.isPending}
              onClick={(e) => {
                e.preventDefault();
                deleteMut.mutate();
              }}
            >
              {deleteMut.isPending && <Loader2 className="size-4 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
